import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  Autocomplete,
  Box,
} from "@mui/material";
import { Button, PhoneField } from "@/components/ui";
import { DEFAULT_PHONE_DIAL_CODE } from "@/constants/phoneDialCodes";
import {
  userService,
  type CreateUserPayload,
  type User,
  type UserRole,
} from "@/services/user.service";
import { customerService, type Customer, type CustomerType } from "@/services/customer.service";
import { useToastStore } from "@/store/toastStore";
import { formatCustomerName } from "@/utils/formatCustomerName";

const ROLE_LABELS: Record<UserRole, string> = {
  ADMIN: "Administrateur",
  MANAGER: "Manager",
  ORDER_CLERK: "Employé commandes",
  DRIVER: "Livreur",
};

const CUSTOMER_TYPES: { value: CustomerType; label: string }[] = [
  { value: "INDIVIDUAL", label: "Particulier" },
  { value: "COMPANY", label: "Entreprise" },
  { value: "INSTITUTION", label: "Institution" },
];

type LinkMode = "none" | "existing" | "new";

export interface CreateUserDialogProps {
  open: boolean;
  onClose: () => void;
  /** Utilisateurs déjà chargés (pour exclure les clients déjà rattachés) */
  users: User[];
}

export function CreateUserDialog({ open, onClose, users }: CreateUserDialogProps): JSX.Element {
  const queryClient = useQueryClient();
  const { success, error: showError } = useToastStore();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<UserRole>("ORDER_CLERK");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneDialCode, setPhoneDialCode] = useState(DEFAULT_PHONE_DIAL_CODE);
  const [phone, setPhone] = useState("");
  const [linkMode, setLinkMode] = useState<LinkMode>("none");
  const [existingCustomer, setExistingCustomer] = useState<Customer | null>(null);
  const [ncFirstName, setNcFirstName] = useState("");
  const [ncLastName, setNcLastName] = useState("");
  const [ncType, setNcType] = useState<CustomerType>("INDIVIDUAL");
  const [ncContactPerson, setNcContactPerson] = useState("");
  const [ncPhoneDialCode, setNcPhoneDialCode] = useState(DEFAULT_PHONE_DIAL_CODE);
  const [ncPhone, setNcPhone] = useState("");
  const [ncEmail, setNcEmail] = useState("");

  const { data: customers = [] } = useQuery({
    queryKey: ["customers", "all"],
    queryFn: () => customerService.getAll(true),
    enabled: open,
  });

  const linkedCustomerIds = useMemo(
    () => new Set(users.map((u) => u.customerId).filter(Boolean) as string[]),
    [users]
  );

  const availableCustomers = useMemo(
    () => customers.filter((c) => !linkedCustomerIds.has(c.id)),
    [customers, linkedCustomerIds]
  );

  useEffect(() => {
    if (!open) return;
    setEmail("");
    setPassword("");
    setRole("ORDER_CLERK");
    setFirstName("");
    setLastName("");
    setPhone("");
    setLinkMode("none");
    setExistingCustomer(null);
    setNcFirstName("");
    setNcLastName("");
    setNcType("INDIVIDUAL");
    setNcContactPerson("");
    setNcPhone("");
    setNcEmail("");
  }, [open]);

  const createMutation = useMutation({
    mutationFn: userService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      success("Utilisateur créé");
      onClose();
    },
    onError: (err: unknown) => {
      const ax = err as { response?: { data?: { message?: string; error?: { message?: string } } } };
      const msg =
        ax?.response?.data?.error?.message ??
        ax?.response?.data?.message ??
        (err instanceof Error ? err.message : "Erreur lors de la création");
      showError(msg);
    },
  });

  const handleSubmit = (): void => {
    if (linkMode === "new" && !ncLastName.trim()) {
      showError("Le nom du nouveau client est requis.");
      return;
    }
    if (linkMode === "existing" && !existingCustomer) {
      showError("Sélectionnez un client ou changez le mode de rattachement.");
      return;
    }

    const payload: CreateUserPayload = {
      email: email.trim(),
      password,
      role,
      firstName: firstName.trim() || undefined,
      lastName: lastName.trim() || undefined,
      phoneDialCode,
      phone: phone.trim() || undefined,
    };

    if (linkMode === "existing" && existingCustomer) {
      payload.customerId = existingCustomer.id;
    }
    if (linkMode === "new") {
      payload.newCustomer = {
        firstName: ncFirstName.trim() || null,
        lastName: ncLastName.trim(),
        type: ncType,
        contactPerson: ncContactPerson.trim() || undefined,
        phoneDialCode: ncPhoneDialCode,
        phone: ncPhone.trim() || undefined,
        email: ncEmail.trim() || undefined,
        isActive: true,
      };
    }

    createMutation.mutate(payload);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Nouvel utilisateur</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            fullWidth
            autoComplete="off"
          />
          <TextField
            label="Mot de passe"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            fullWidth
            helperText="Au moins 8 caractères, une majuscule, une minuscule et un chiffre"
            autoComplete="new-password"
          />
          <FormControl fullWidth size="small">
            <InputLabel>Rôle</InputLabel>
            <Select
              label="Rôle"
              value={role}
              onChange={(e) => setRole(e.target.value as UserRole)}
            >
              {(Object.keys(ROLE_LABELS) as UserRole[]).map((r) => (
                <MenuItem key={r} value={r}>
                  {ROLE_LABELS[r]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Prénom"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            fullWidth
          />
          <TextField
            label="Nom"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            fullWidth
          />
          <PhoneField
            dialCode={phoneDialCode}
            phone={phone}
            onDialCodeChange={setPhoneDialCode}
            onPhoneChange={setPhone}
          />

          <Typography variant="subtitle2" sx={{ mt: 1 }}>
            Rattachement à une fiche client (contact)
          </Typography>
          <RadioGroup
            value={linkMode}
            onChange={(_, v) => {
              setLinkMode(v as LinkMode);
              setExistingCustomer(null);
            }}
          >
            <FormControlLabel value="none" control={<Radio size="small" />} label="Aucun (compte interne)" />
            <FormControlLabel
              value="existing"
              control={<Radio size="small" />}
              label="Client existant"
            />
            <FormControlLabel value="new" control={<Radio size="small" />} label="Nouveau client" />
          </RadioGroup>

          {linkMode === "existing" && (
            <Autocomplete
              options={availableCustomers}
              value={existingCustomer}
              onChange={(_, v) => setExistingCustomer(v)}
              getOptionLabel={(c) =>
                [formatCustomerName(c), c.email].filter(Boolean).join(" — ") || c.id
              }
              renderInput={(params) => (
                <TextField {...params} label="Client" placeholder="Rechercher…" />
              )}
              noOptionsText="Aucun client disponible (déjà lié ou liste vide)"
            />
          )}

          {linkMode === "new" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pl: 0 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Type</InputLabel>
                <Select
                  label="Type"
                  value={ncType}
                  onChange={(e) => setNcType(e.target.value as CustomerType)}
                >
                  {CUSTOMER_TYPES.map((t) => (
                    <MenuItem key={t.value} value={t.value}>
                      {t.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {ncType === "INDIVIDUAL" && (
                <TextField
                  label="Prénom du client"
                  value={ncFirstName}
                  onChange={(e) => setNcFirstName(e.target.value)}
                  fullWidth
                />
              )}
              <TextField
                label={ncType === "INDIVIDUAL" ? "Nom du client" : "Dénomination"}
                value={ncLastName}
                onChange={(e) => setNcLastName(e.target.value)}
                required
                fullWidth
              />
              <TextField
                label="Personne de contact"
                value={ncContactPerson}
                onChange={(e) => setNcContactPerson(e.target.value)}
                fullWidth
              />
              <PhoneField
                label="Téléphone (client)"
                dialCode={ncPhoneDialCode}
                phone={ncPhone}
                onDialCodeChange={setNcPhoneDialCode}
                onPhoneChange={setNcPhone}
              />
              <TextField
                label="Email (client)"
                type="email"
                value={ncEmail}
                onChange={(e) => setNcEmail(e.target.value)}
                fullWidth
              />
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="outline" onClick={onClose} disabled={createMutation.isPending}>
          Annuler
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          loading={createMutation.isPending}
          disabled={!email.trim() || !password}
        >
          Créer
        </Button>
      </DialogActions>
    </Dialog>
  );
}
